// 세션 창으로 넘기기 실패 알림(통합앱) — 2026-09-17 결함 수정.
//
// 종전에는 넘기기 결과를 보지 않아, 세션 창의 웹 화면이 죽어 있으면 접속 버튼이 **아무 반응 없이** 끝났다.
// 이제 실패를 받아 이유와 다음 행동을 보여 준다:
//   - 응답 없음: [세션 창 다시 열기] — 죽은 창을 없애고 새 창에서 연다(그 창의 남은 탭은 닫힌다고 미리 말한다).
//   - 그 밖의 오류: 메시지 + [다시 시도](같은 방법으로 한 번 더).
import { useState } from 'react';
import type { SessionOpenFailure } from './bridge';

/** 화면 문구 — 한 곳에서 관리한다. */
const TEXT = {
  unresponsive: '원격 세션 창이 응답하지 않아 접속을 넘기지 못했습니다.',
  unresponsiveHint: '세션 창을 다시 열면 응답 없는 창과 그 안의 탭은 닫힙니다.',
  failed: '원격 세션 창을 열지 못했습니다',
  reopen: '세션 창 다시 열기',
  retry: '다시 시도',
  working: '여는 중…',
  dismiss: '닫기',
} as const;

export interface SessionOpenAlertProps {
  /** 대상 이름(예: "075-238-758") — 어떤 접속이 실패했는지 보여 준다. */
  label: string;
  failure: SessionOpenFailure;
  /** 응답 없음 → 창을 새로 만들어 연다. 실패하면 reject(부모가 알림을 바꾼다). */
  onReopen: () => Promise<void>;
  /** 그 밖의 오류 → 같은 방법으로 한 번 더. */
  onRetry: () => void;
  onDismiss: () => void;
}

export function SessionOpenAlert({ label, failure, onReopen, onRetry, onDismiss }: SessionOpenAlertProps) {
  const [busy, setBusy] = useState(false);
  const unresponsive = failure.kind === 'unresponsive';

  const act = () => {
    if (!unresponsive) {
      onRetry();
      return;
    }
    setBusy(true);
    // 성공·실패 모두 부모가 이 알림을 바꾸거나 치운다 — 여기서는 버튼 잠금만 푼다.
    void onReopen().finally(() => setBusy(false));
  };

  return (
    <div className="session-open-alert" role="alert">
      <div>
        <strong>{label}</strong> —{' '}
        {unresponsive ? TEXT.unresponsive : `${TEXT.failed}: ${failure.kind === 'error' ? failure.message : ''}`}
      </div>
      {unresponsive && <div className="hint">{TEXT.unresponsiveHint}</div>}
      <div className="row">
        <button onClick={act} disabled={busy}>
          {busy ? TEXT.working : unresponsive ? TEXT.reopen : TEXT.retry}
        </button>
        <button className="ghost" onClick={onDismiss} disabled={busy}>
          {TEXT.dismiss}
        </button>
      </div>
    </div>
  );
}
